import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useContext, useCallback, useMemo } from 'react';
import { assertContext, ResolveContext } from './context';
import { firstOfType } from '@resolve-js/core';
import { useClient } from './use-client';
import { isCallback, isOptions, isSerializableMap } from './generic';

function useViewModel(modelName, aggregateIds, args, stateChangeCallback, eventReceivedCallback, queryOptions) {
  var context = useContext(ResolveContext);
  assertContext(context);
  var client = useClient();
  var viewModels = context.viewModels;
  var viewModel = viewModels.find(function (_ref) {
    var name = _ref.name;
    return name === modelName;
  });

  if (!viewModel) {
    throw Error("View model " + modelName + " not exist within context");
  }

  var actualArgs = isSerializableMap(args) ? args : undefined;
  var actualQueryOptions = firstOfType(isOptions, stateChangeCallback, eventReceivedCallback, queryOptions);
  var actualStateChangeCallback = firstOfType(isCallback, args, stateChangeCallback);
  var actualEventReceivedCallback = firstOfType(isCallback, eventReceivedCallback, stateChangeCallback);
  actualEventReceivedCallback = actualEventReceivedCallback !== actualStateChangeCallback ? actualEventReceivedCallback : undefined;

  if (!actualStateChangeCallback) {
    throw Error("state change callback required");
  }

  var closure = useMemo(function () {
    var initialState = viewModel.projection.Init ? viewModel.projection.Init() : null;
    return {
      initialState: initialState,
      cursor: null
    };
  }, []);
  var setState = useCallback(function (state, initial) {
    closure.state = state;
    actualStateChangeCallback(closure.state, initial);
  }, []);
  var queryState = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var result, data, _result$meta, url, cursor, _aggregateIds;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return client.query({
              name: modelName,
              aggregateIds: aggregateIds,
              args: actualArgs
            }, actualQueryOptions);

          case 2:
            result = _context.sent;

            if (result) {
              data = result.data, _result$meta = result.meta;
              _result$meta = _result$meta === void 0 ? {} : _result$meta;
              url = _result$meta.url, cursor = _result$meta.cursor, _aggregateIds = _result$meta.aggregateIds;
              setState(data, false);
              closure.url = url;
              closure.cursor = cursor !== null && cursor !== void 0 ? cursor : null;
              closure.aggregateIds = _aggregateIds;
            }

          case 4:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), []);
  var applyEvent = useCallback(function (event) {
    if (isCallback(actualEventReceivedCallback)) {
      actualEventReceivedCallback(event);
    }

    setState(viewModel.projection[event.type](closure.state, event), false);
  }, []);
  var connect = useCallback(function (done) {
    var asyncConnect = /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var _closure$url;

        var subscribe, subscription;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return queryState();

              case 2:
                subscribe = client.subscribe((_closure$url = closure.url) !== null && _closure$url !== void 0 ? _closure$url : '', closure.cursor, modelName, Array.isArray(closure.aggregateIds) ? closure.aggregateIds : aggregateIds, function (event) {
                  return applyEvent(event);
                }, undefined, function () {
                  return queryState();
                });
                _context2.next = 5;
                return subscribe;

              case 5:
                subscription = _context2.sent;

                if (subscription) {
                  closure.subscription = subscription;
                }

                return _context2.abrupt("return", subscription);

              case 8:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function asyncConnect() {
        return _ref3.apply(this, arguments);
      };
    }();

    setState(closure.initialState, true);

    if (typeof done !== 'function') {
      return asyncConnect();
    }

    asyncConnect().then(function (result) {
      return done(null, result);
    })["catch"](function (error) {
      return done(error, null);
    });
    return undefined;
  }, []);
  var dispose = useCallback(function (done) {
    var asyncDispose = /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                if (!closure.subscription) {
                  _context3.next = 3;
                  break;
                }

                _context3.next = 3;
                return client.unsubscribe(closure.subscription);

              case 3:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3);
      }));

      return function asyncDispose() {
        return _ref4.apply(this, arguments);
      };
    }();

    if (typeof done !== 'function') {
      return asyncDispose();
    }

    asyncDispose().then(function () {
      return done();
    })["catch"](function (error) {
      return done(error);
    });
    return undefined;
  }, []);
  return useMemo(function () {
    return {
      connect: connect,
      dispose: dispose,
      initialState: closure.initialState
    };
  }, []);
}

export { useViewModel };
//# sourceMappingURL=use-view-model.js.map